import React, { useState, useEffect } from 'react';
import { Box, Heading, Text, useBreakpoint } from '@landr/maestro';
import { PlanFrequencyEnum } from '@landr/core.models';
import { DistributionFeaturesTable } from '@landr/pricing';
import { PricingTabDocument } from '../types';
import { usePricingContext } from '../common/PricingContext';
import { getTabDataByUID, isPricingTabOfTypeDistribution } from '../common/helpers';
import { StyledPricingTabContent, StyledPricingTabPlans } from '../styled';
import { PricingPlan } from './PricingPlan';
import '../Pricing.scss';

const frequencySwitch = (currentFequency: PlanFrequencyEnum) => {
    return currentFequency === PlanFrequencyEnum.Yearly ? PlanFrequencyEnum.Monthly : PlanFrequencyEnum.Yearly;
};

const PlanFrequencyToggleRequired = {
    Monthly: false,
    Yearly: false,
    Both: true,
};

const checkFrequencyToggleRequired = (tabData: '' | PricingTabDocument | null) => {
    return tabData && PlanFrequencyToggleRequired[(tabData as PricingTabDocument).frequencies_available];
};

export type PricingTabProps = { tab: PricingTabDocument; languageCode: string };

export const PricingTab: React.FC<PricingTabProps> = ({ tab, languageCode }) => {
    const { pricingTabsData, translations } = usePricingContext();
    const currentPricingTabData = getTabDataByUID(pricingTabsData, tab._meta.uid);
    const [frequency, setFrequency] = useState(PlanFrequencyEnum.Yearly);
    const [isFrequencyToggleAvailable, setIsFrequencyToggleAvailable] = useState(false);
    const [isDistributionTab, setIsDistributionTab] = useState(false);
    const [yearlySaving, setYearlySaving] = useState(0);
    const isMobile = useBreakpoint('md');

    useEffect(() => {
        if (currentPricingTabData) {
            if (checkFrequencyToggleRequired(currentPricingTabData)) {
                // If the service offer in the state is Mastering, enable the frequency toggle
                setIsFrequencyToggleAvailable(true);
            }
            setIsDistributionTab(isPricingTabOfTypeDistribution(currentPricingTabData));
        }
    }, [currentPricingTabData]);

    return (
        <>
            <Heading>
                <Box paddingY="xl" display="flex" justifyContent="center" flexDirection={'column'}>
                    {isFrequencyToggleAvailable && (
                        <>
                            <Box display="flex" justifyContent="center">
                                <Text size={'lg'} paddingRight={'lg'}>
                                    {translations.toggle_monthly}
                                </Text>
                                <Box width="75px">
                                    {/* // TODO ECO-1938: replace with maestro toggle component */}
                                    <Box className="PricingTable-frequency Switch">
                                        <input
                                            type="checkbox"
                                            id="frequency-switch"
                                            aria-label="frequency-toggle"
                                            checked={frequency === PlanFrequencyEnum.Yearly}
                                            onClick={() => {
                                                setFrequency((state) => {
                                                    const nextFrequency = frequencySwitch(state);
                                                    // TODO: DIS-627 Implement pricing routing
                                                    // updatePath(currentPricingTabData._meta.uid, nextFrequency);
                                                    return nextFrequency;
                                                });
                                            }}
                                            onChange={() => null}
                                        />

                                        <label aria-label={`toggle-${frequency}`} htmlFor="frequency-switch"></label>
                                    </Box>
                                </Box>
                                <Text size={'lg'} paddingLeft={'lg'}>
                                    {translations.toggle_yearly}
                                </Text>
                            </Box>
                            {!isMobile && (
                                <Box display="flex" justifyContent="center" mt={15}>
                                    <Text color={'primary.500'}>
                                        <b>
                                            {translations.save_up_to} {yearlySaving}%
                                        </b>{' '}
                                        {translations.on_yearly_plans}
                                    </Text>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Heading>
            {currentPricingTabData && (
                <StyledPricingTabContent aria-label="pricing-tab" display="flex" flexDirection="column">
                    <StyledPricingTabPlans>
                        {currentPricingTabData.plans.map(({ plan }) => {
                            return (
                                <PricingPlan
                                    key={plan.name}
                                    plan={plan}
                                    frequency={frequency}
                                    setYearlySaving={setYearlySaving}
                                    languageCode={languageCode}
                                ></PricingPlan>
                            );
                        })}
                    </StyledPricingTabPlans>
                    {isDistributionTab && (
                        <Box padding={'jb'}>
                            <DistributionFeaturesTable />
                        </Box>
                    )}
                </StyledPricingTabContent>
            )}
        </>
    );
};
